import React from 'react';
import { Card, CardContent } from '../components/shadcn/card';
import { Separator } from '../components/shadcn/separator';
import LegalPageLayout from '../components/LegalPageLayout';

const Terms: React.FC = () => {
  return (
    <LegalPageLayout
      badge="Legal"
      title="Website Terms & Conditions"
      subtitle="Please read these terms carefully before using our services"
      effectiveDate="1 January 2026"
      lastUpdated="March 2026"
      currentPage="terms"
    >
      <section>
        <h2 className="font-display text-2xl md:text-3xl font-bold text-text mb-4 tracking-tight">1. Acceptance of Terms</h2>
        <p>
          By accessing and using the Nucleus Systems website (
          <a href="https://www.nucleus-systems.com" target="_blank" rel="noopener noreferrer" className="text-brand hover:underline">
            www.nucleus-systems.com
          </a>
          ) and the Paxley software platform ("Service"), you accept and agree to be bound by these
          Terms and Conditions ("Terms"). If you do not agree to these Terms, please do not use our
          Service.
        </p>
        <p className="mt-4">
          These Terms constitute a legally binding agreement between you ("User", "Client", or "you")
          and Nucleus Systems (Pty) Ltd ("Nucleus Systems", "we", "us", or "our"), a company
          incorporated in South Africa.
        </p>
      </section>

      <Separator />

      <section>
        <h2 className="font-display text-2xl md:text-3xl font-bold text-text mb-4 tracking-tight">2. Definitions</h2>
        <p>For the purposes of these Terms:</p>
        <ul className="list-disc list-inside space-y-2 mt-4">
          <li><strong className="text-text">"Service"</strong> refers to the Paxley code security scanning platform, related software, APIs, and all associated features provided by Nucleus Systems.</li>
          <li><strong className="text-text">"User" or "Client"</strong> refers to any individual or organisation accessing or using the Service.</li>
          <li><strong className="text-text">"Subscription"</strong> refers to a paid or trial plan granting access to the Service.</li>
          <li><strong className="text-text">"Content"</strong> refers to any data, materials, or outputs generated through the use of the Service.</li>
          <li><strong className="text-text">"Account"</strong> refers to the registered user profile created to access the Service.</li>
        </ul>
      </section>

      <Separator />

      <section>
        <h2 className="font-display text-2xl md:text-3xl font-bold text-text mb-4 tracking-tight">3. Scope of Service</h2>
        <p>
          Nucleus Systems provides software security scanning tools under the Paxley brand. The Service
          enables users to scan GitHub repositories for security vulnerabilities, receive scan
          findings, and manage security risk within their software development workflows.
        </p>
        <p className="mt-4">
          We reserve the right to modify, suspend, or discontinue any aspect of the Service at any
          time, with reasonable notice to active subscribers where possible.
        </p>
      </section>

      <Separator />

      <section>
        <h2 className="font-display text-2xl md:text-3xl font-bold text-text mb-4 tracking-tight">4. Account Registration</h2>
        <p>To use the Service, you must register for an account using GitHub OAuth. You agree to:</p>
        <ul className="list-disc list-inside space-y-2 mt-4">
          <li>Provide accurate and complete registration information.</li>
          <li>Maintain the security of your account credentials.</li>
          <li>Notify us immediately of any unauthorised use of your account.</li>
          <li>Accept responsibility for all activities that occur under your account.</li>
        </ul>
        <p className="mt-4">
          Nucleus Systems reserves the right to suspend or terminate accounts found to be in violation
          of these Terms.
        </p>
      </section>

      <Separator />

      <section>
        <h2 className="font-display text-2xl md:text-3xl font-bold text-text mb-4 tracking-tight">5. Licence to Use</h2>
        <p>
          Subject to these Terms and payment of applicable fees, Nucleus Systems grants you a limited,
          non-exclusive, non-transferable, revocable licence to access and use the Service for your
          internal business purposes.
        </p>
        <p className="mt-4">This licence does not permit you to:</p>
        <ul className="list-disc list-inside space-y-2 mt-4">
          <li>Sublicense, sell, resell, transfer, assign, or otherwise commercially exploit the Service.</li>
          <li>Reverse engineer, decompile, or otherwise attempt to extract source code from the Service.</li>
          <li>Reproduce, duplicate, copy, or exploit any portion of the Service without prior written permission.</li>
          <li>Use the Service in any way that violates applicable laws or regulations.</li>
        </ul>
      </section>

      <Separator />

      <section>
        <h2 className="font-display text-2xl md:text-3xl font-bold text-text mb-4 tracking-tight">6. Usage Restrictions &amp; Acceptable Use</h2>
        <p>You agree not to use the Service in any manner that:</p>
        <ul className="list-disc list-inside space-y-2 mt-4">
          <li>Violates any applicable local, national, or international law or regulation.</li>
          <li>Is fraudulent, false, misleading, or deceptive.</li>
          <li>Infringes the intellectual property rights of Nucleus Systems or any third party.</li>
          <li>Involves the unauthorised scanning or accessing of third-party systems or repositories.</li>
          <li>Attempts to gain unauthorised access to any part of the Service or its supporting infrastructure.</li>
          <li>Transmits any malware, viruses, or other malicious code.</li>
          <li>Interferes with the proper working of the Service or imposes an unreasonable load on our infrastructure.</li>
        </ul>
        <p className="mt-4">
          Violation of these restrictions may result in immediate suspension or termination of your
          account without notice or refund.
        </p>
      </section>

      <Separator />

      <section>
        <h2 className="font-display text-2xl md:text-3xl font-bold text-text mb-4 tracking-tight">7. GitHub Integration</h2>
        <p>
          The Service integrates with GitHub via OAuth. By connecting your GitHub account, you
          authorise Nucleus Systems to access repository data solely for the purposes of providing the
          scanning service. We access only the repositories you explicitly authorise, and we do not
          store source code persistently. You may revoke access at any time through GitHub account
          settings.
        </p>
      </section>

      <Separator />

      <section>
        <h2 className="font-display text-2xl md:text-3xl font-bold text-text mb-4 tracking-tight">8. Subscriptions &amp; Billing</h2>
        <p>
          Access to the Service's paid features is subject to the purchase of a Subscription. By
          subscribing, you agree to the following:
        </p>
        <ul className="list-disc list-inside space-y-2 mt-4">
          <li>Subscription fees are charged in advance on a monthly or annual basis as selected.</li>
          <li>All payments are processed securely by <strong className="text-text">PAYSTACK</strong>. Nucleus Systems does not store your payment card information.</li>
          <li>Fees are non-refundable except as expressly stated in our <a href="/refund" className="text-brand hover:underline">Refund &amp; Returns Policy</a>.</li>
          <li>We reserve the right to change our pricing with 30 days' notice to active subscribers.</li>
          <li>Failure to maintain a valid payment method may result in suspension of your account.</li>
        </ul>
      </section>

      <Separator />

      <section>
        <h2 className="font-display text-2xl md:text-3xl font-bold text-text mb-4 tracking-tight">9. Intellectual Property</h2>
        <p>
          All intellectual property rights in the Service, including but not limited to software,
          trademarks, logos, and documentation, are owned by or licensed to Nucleus Systems. Nothing in
          these Terms grants you any right, title, or interest in or to the intellectual property of
          Nucleus Systems.
        </p>
        <p className="mt-4">
          You retain ownership of any data, repositories, or content you submit to the Service. You
          grant Nucleus Systems a limited licence to process such content solely for the purpose of
          delivering the Service.
        </p>
      </section>

      <Separator />

      <section>
        <h2 className="font-display text-2xl md:text-3xl font-bold text-text mb-4 tracking-tight">10. Confidentiality</h2>
        <p>
          Nucleus Systems treats your repository data and scan results as confidential. We do not
          share, sell, or disclose your code or scan findings to third parties except as required to
          deliver the Service or as required by law. Our staff is bound by confidentiality obligations
          with respect to client data.
        </p>
      </section>

      <Separator />

      <section>
        <h2 className="font-display text-2xl md:text-3xl font-bold text-text mb-4 tracking-tight">11. Privacy</h2>
        <p>
          Your use of the Service is governed by our{' '}
          <a href="/privacy" className="text-brand hover:underline">Privacy Policy</a>, which is
          incorporated into these Terms by reference. By using the Service, you consent to the
          collection and use of information as described in our Privacy Policy.
        </p>
      </section>

      <Separator />

      <section>
        <h2 className="font-display text-2xl md:text-3xl font-bold text-text mb-4 tracking-tight">12. Service Availability &amp; Uptime</h2>
        <p>
          Nucleus Systems will endeavour to make the Service available at all times but does not
          guarantee uninterrupted or error-free access. The Service may be temporarily unavailable due
          to scheduled maintenance, unplanned outages, or factors beyond our reasonable control.
        </p>
        <p className="mt-4">
          We will make reasonable efforts to provide advance notice of planned maintenance windows
          where possible. Nucleus Systems shall not be liable for any loss or damage arising from
          Service unavailability.
        </p>
      </section>

      <Separator />

      <section>
        <h2 className="font-display text-2xl md:text-3xl font-bold text-text mb-4 tracking-tight">13. Disclaimer of Warranties</h2>
        <p>
          The Service is provided on an "as is" and "as available" basis without warranties of any
          kind, either express or implied, including but not limited to implied warranties of
          merchantability, fitness for a particular purpose, or non-infringement.
        </p>
        <p className="mt-4">
          Nucleus Systems does not warrant that: (a) the Service will meet all of your requirements;
          (b) the Service will be uninterrupted, timely, or error-free; or (c) any scan results will
          be complete, accurate, or exhaustive in identifying all security vulnerabilities.
        </p>
      </section>

      <Separator />

      <section>
        <h2 className="font-display text-2xl md:text-3xl font-bold text-text mb-4 tracking-tight">14. Limitation of Liability</h2>
        <p>
          To the maximum extent permitted by applicable law, Nucleus Systems shall not be liable for
          any indirect, incidental, special, consequential, or punitive damages, or any loss of profits
          or revenue, whether incurred directly or indirectly.
        </p>
        <p className="mt-4">
          Our total cumulative liability to you for any claim arising out of or relating to these
          Terms or the Service shall not exceed the fees paid by you to Nucleus Systems in the 3-month
          period immediately preceding the event giving rise to the claim.
        </p>
        <p className="mt-4">
          Nothing in these Terms shall limit or exclude liability for death or personal injury caused
          by negligence, fraud, or any other liability that cannot be excluded by applicable law.
        </p>
      </section>

      <Separator />

      <section>
        <h2 className="font-display text-2xl md:text-3xl font-bold text-text mb-4 tracking-tight">15. Indemnification</h2>
        <p>
          You agree to indemnify, defend, and hold harmless Nucleus Systems and its officers,
          directors, employees, and agents from and against any claims, liabilities, damages, losses,
          and expenses (including reasonable legal fees) arising from or in connection with your use of
          the Service, violation of these Terms, or infringement of any third-party rights.
        </p>
      </section>

      <Separator />

      <section>
        <h2 className="font-display text-2xl md:text-3xl font-bold text-text mb-4 tracking-tight">16. Termination</h2>
        <p>
          Either party may terminate these Terms or your access to the Service at any time. Nucleus
          Systems may suspend or terminate your account immediately without notice if you breach these
          Terms.
        </p>
        <p className="mt-4">
          Upon termination: (a) your licence to use the Service will cease; (b) you must cease all use
          of the Service; and (c) any outstanding fees will become immediately due and payable.
        </p>
        <p className="mt-4">Termination shall not affect any rights or obligations that have accrued prior to termination.</p>
      </section>

      <Separator />

      <section>
        <h2 className="font-display text-2xl md:text-3xl font-bold text-text mb-4 tracking-tight">17. Governing Law &amp; Dispute Resolution</h2>
        <p>
          These Terms are governed by and construed in accordance with the laws of the Republic of
          South Africa. Any disputes arising under or in connection with these Terms shall be subject
          to the exclusive jurisdiction of the courts of South Africa.
        </p>
        <p className="mt-4">
          Before initiating legal proceedings, the parties agree to attempt to resolve any dispute
          through good-faith negotiations for 30 days.
        </p>
      </section>

      <Separator />

      <section>
        <h2 className="font-display text-2xl md:text-3xl font-bold text-text mb-4 tracking-tight">18. Changes to These Terms</h2>
        <p>
          Nucleus Systems reserves the right to modify these Terms at any time. We will notify users
          of material changes by posting the updated Terms on our website and, where appropriate, by
          email notification to registered users.
        </p>
        <p className="mt-4">
          Your continued use of the Service after the effective date of revised Terms constitutes your
          acceptance of those changes. We recommend reviewing these Terms periodically.
        </p>
      </section>

      <Separator />

      <section>
        <h2 className="font-display text-2xl md:text-3xl font-bold text-text mb-4 tracking-tight">19. Contact Information</h2>
        <p>For questions or concerns regarding these Terms and Conditions:</p>
        <Card className="border-border/30 bg-surface2 mt-4">
          <CardContent className="p-6 space-y-2 text-sm">
            <p><strong className="text-text">Contact:</strong> Nucleus Systems (Pty) Ltd</p>
            <p>43 Silver Creek Eco Park Estates</p>
            <p><strong className="text-text">Tel:</strong> +27 83 680 3427</p>
            <p>
              <strong className="text-text">Email:</strong>{' '}
              <a href="mailto:godfrey.kutumela@nucleus-systems.com" className="text-brand hover:underline">
                godfrey.kutumela@nucleus-systems.com
              </a>
            </p>
            <p>
              <strong className="text-text">Web:</strong>{' '}
              <a href="https://www.nucleus-systems.com" target="_blank" rel="noopener noreferrer" className="text-brand hover:underline">
                www.nucleus-systems.com
              </a>
            </p>
            <p><strong className="text-text">Web Developer:</strong> Godfrey Kutumela</p>
          </CardContent>
        </Card>
      </section>
    </LegalPageLayout>
  );
};

export default Terms;
